import React, { useState, useRef, useCallback, useEffect, lazy, Suspense } from 'react';
import {
  BarChart3,
  Upload,
  X,
  FileText,
  FileSpreadsheet,
  Presentation,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  DollarSign,
  Users,
  ChevronDown,
  ChevronUp,
  Download,
  Plus,
  RefreshCw,
  AlertCircle,
  Loader2,
  Eye,
  Trash2,
  Flag,
  Pencil,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import ConflictResolutionPanel from '../components/Portfolio/ConflictResolutionPanel';

const ExecutiveBriefView = lazy(() => import('../components/Portfolio/ExecutiveBriefView'));

// ─── Types ────────────────────────────────────────────────────────────────────

type RAGStatus = 'ON_TRACK' | 'MONITOR' | 'ATTENTION';
type VendorStatus = 'pending' | 'processing' | 'ready' | 'error' | 'review_required';
type FileFormat = 'xlsx' | 'docx' | 'pptx' | 'email' | 'pdf' | 'txt' | 'csv';
type SeverityLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
type DeliverableStatus = 'NOT_STARTED' | 'PREP_IN_PROGRESS' | 'IN_PROGRESS' | 'COMPLETE' | 'MISSED' | 'AT_RISK';

interface VendorSummary {
  id: string;
  name: string;
  contractNumber?: string;
  rag: RAGStatus | null;
  status: VendorStatus;
  fileName: string;
  fileFormat: FileFormat;
  uploadedAt: string;
  confidence: number | null;
  flaggedFields: number;
  validationErrors: string[];
  contractCapFees?: number;
  contractCapExpenses?: number;
  incurredFeesToDate?: number;
  incurredExpensesToDate?: number;
  pendingApprovalExpenses?: number;
  budgetImpactThisPeriod?: number;
  reportPeriodEnd?: string;
  expensePeriodEnd?: string;
  preparedBy?: string;
}

interface RiskRecord {
  riskId: string;
  vendorName: string;
  severity: SeverityLevel;
  track: string;
  title: string;
  impact: string;
  owner: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'MITIGATED' | 'CLOSED';
  openSince: string;
}

interface DecisionRecord {
  decisionId: string;
  vendorName: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM';
  title: string;
  context: string;
  owner: string;
  neededBy: string;
}

interface MilestoneRecord {
  deliverableId: string;
  vendorName: string;
  deliverableName: string;
  contractedDueDate: string;
  milestonePaymentAmount?: number;
  status: DeliverableStatus;
  completionPct?: number;
  milestoneType?: string;
  isApproximate?: boolean;
  changeLog?: any[];
}

interface MasterDeliverable {
  id: number;
  vendorName: string;
  vendorKey: string;
  parentId: number | null;
  tier: 'CONTRACTUAL' | 'SUB';
  deliverableName: string;
  contractedDueDate: string | null;
  milestonePaymentAmount?: number;
  derivedStatus: DeliverableStatus;
  completionPct?: number;
  milestoneType?: string;
  children: MasterDeliverable[];
}

interface MasterVendorGroup {
  vendorName: string;
  vendorKey: string;
  deliverables: MasterDeliverable[];
}

interface FlaggedField {
  vendorId: string;
  vendorName: string;
  fieldName: string;
  confidence: number;
  extractedValue: string;
  sourceText: string;
}

interface ConflictCandidate {
  value: string;
  source_location: string;
  raw_snippet?: string;
}

interface SourceConflict {
  id: number;
  vendorReportId: number;
  vendorName: string;
  fieldKey: string;
  fieldLabel: string;
  conflictType: string;
  candidates: ConflictCandidate[];
  autoSuggested: string | null;
  chosenValue: string | null;
  resolved: boolean;
}

interface EnvelopeRollup {
  envelopeId: string;
  envelopeName: string;
  objective: string | null;
  authorizedBudget: number;
  targetCompletionDate: string | null;
  vendorCount: number;
  committedBudget: number;
  incurred: number;
  pending: number;
  committed: number;
  rag: RAGStatus;
  burnPct: number;
}

type Tab = 'overview' | 'brief' | 'vendors' | 'risks' | 'milestones' | 'expenses' | 'conflicts';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const RAG_CONFIG: Record<RAGStatus, { label: string; bg: string; text: string; dot: string }> = {
  ON_TRACK: { label: 'On Track', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  MONITOR:  { label: 'Monitor',  bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  ATTENTION:{ label: 'Attention',bg: 'bg-red-50',    text: 'text-red-700',    dot: 'bg-red-500'    },
};

const SEVERITY_CONFIG: Record<string, { bg: string; text: string }> = {
  CRITICAL: { bg: 'bg-red-100',    text: 'text-red-700'    },
  HIGH:     { bg: 'bg-orange-100', text: 'text-orange-700' },
  MEDIUM:   { bg: 'bg-amber-100',  text: 'text-amber-700'  },
  LOW:      { bg: 'bg-blue-100',   text: 'text-blue-700'   },
};
const severityCfg = (key: string) => SEVERITY_CONFIG[key] ?? SEVERITY_CONFIG['MEDIUM'];

const MILESTONE_STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  NOT_STARTED:      { label: 'Not Started',      bg: 'bg-gray-100',   text: 'text-gray-600'   },
  PREP_IN_PROGRESS: { label: 'Prep In Progress', bg: 'bg-blue-100',   text: 'text-blue-700'   },
  IN_PROGRESS:      { label: 'In Progress',      bg: 'bg-indigo-100', text: 'text-indigo-700' },
  REVIEW_REQUIRED:  { label: 'Review Required',  bg: 'bg-teal-100',   text: 'text-teal-700'   },
  COMPLETE:         { label: 'Complete',          bg: 'bg-green-100',  text: 'text-green-700'  },
  MISSED:           { label: 'Missed',            bg: 'bg-red-100',    text: 'text-red-700'    },
  AT_RISK:          { label: 'At Risk',           bg: 'bg-orange-100', text: 'text-orange-700' },
};

const MILESTONE_TYPE_CFG: Record<string, { label: string; cls: string }> = {
  SOW_DELIVERABLE:     { label: 'SOW',        cls: 'bg-blue-50 text-blue-600'   },
  REGULATORY_DEADLINE: { label: 'Regulatory', cls: 'bg-red-50 text-red-600'     },
  PROGRAM_MILESTONE:   { label: 'Program',    cls: 'bg-gray-100 text-gray-500'  },
};
const milestoneCfg = (key: string) => MILESTONE_STATUS_CONFIG[key] ?? MILESTONE_STATUS_CONFIG['NOT_STARTED'];

const FILE_ICON: Record<FileFormat, React.ElementType> = {
  xlsx:  FileSpreadsheet,
  docx:  FileText,
  pptx:  Presentation,
  email: FileText,
  pdf:   FileText,
  txt:   FileText,
  csv:   FileSpreadsheet,
};

function formatCurrency(value?: number): string {
  if (value == null) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${value.toLocaleString()}`;
  return `$${value.toFixed(2)}`;
}

function formatPct(numerator?: number, denominator?: number): string {
  if (numerator == null || !denominator) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

const DeliverableEditForm: React.FC<{
  item: MasterDeliverable;
  onSave: (patch: Record<string, any>) => void;
  onCancel: () => void;
}> = ({ item, onSave, onCancel }) => {
  const [name, setName] = useState(item.deliverableName);
  const [status, setStatus] = useState(item.derivedStatus);
  const [pct, setPct] = useState(item.completionPct != null ? String(item.completionPct) : '');
  const [due, setDue] = useState(item.contractedDueDate || '');
  const [payment, setPayment] = useState(item.milestonePaymentAmount != null ? String(item.milestonePaymentAmount) : '');
  const [type, setType] = useState(item.milestoneType || 'SOW_DELIVERABLE');

  const submit = () => {
    if (!name.trim()) { toast.error('Deliverable name cannot be empty'); return; }
    const patch: Record<string, any> = {};
    if (name.trim() !== item.deliverableName) patch.deliverable_name = name.trim();
    if (status !== item.derivedStatus) patch.derived_status = status;
    const pctNum = pct === '' ? null : Number(pct);
    if (pctNum !== (item.completionPct ?? null)) {
      if (pctNum !== null && (isNaN(pctNum) || pctNum < 0 || pctNum > 100)) { toast.error('Completion % must be 0-100'); return; }
      patch.completion_pct = pctNum;
    }
    if ((due || '') !== (item.contractedDueDate || '')) patch.contracted_due_date = due || null;
    const payNum = payment === '' ? null : Number(payment);
    if (payNum !== (item.milestonePaymentAmount ?? null)) {
      if (payNum !== null && (isNaN(payNum) || payNum < 0)) { toast.error('Payment must be a non-negative number'); return; }
      patch.milestone_payment_amount = payNum;
    }
    if (type !== (item.milestoneType || 'SOW_DELIVERABLE')) patch.milestone_type = type;
    if (Object.keys(patch).length === 0) { onCancel(); return; }
    onSave(patch);
  };

  return (
    <div className="px-5 py-4 bg-indigo-50/40 border-y border-indigo-100">
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12">
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Deliverable name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value as DeliverableStatus)}
            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200">
            {Object.entries(MILESTONE_STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="col-span-6 sm:col-span-2">
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">% Complete</label>
          <input type="number" min={0} max={100} value={pct} onChange={e => setPct(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Due date</label>
          <input type="date" value={due ? due.slice(0, 10) : ''} onChange={e => setDue(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </div>
        <div className="col-span-6 sm:col-span-2">
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Payment</label>
          <input type="number" min={0} value={payment} onChange={e => setPayment(e.target.value)} placeholder="—"
            className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Type</label>
          <select value={type} onChange={e => setType(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200">
            {Object.entries(MILESTONE_TYPE_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={submit}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700">
          <Check className="w-3.5 h-3.5" /> Save
        </button>
        <button onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
};

const MasterDeliverablesTree: React.FC<{
  groups: MasterVendorGroup[];
  onUpdate: (id: number, patch: Record<string, any>) => void;
}> = ({ groups, onUpdate }) => {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const toggle = (id: number) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const saveEdit = (id: number, patch: Record<string, any>) => { onUpdate(id, patch); setEditingId(null); };

  if (groups.length === 0) return null;

  const statusBadge = (status: string) => (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${milestoneCfg(status).bg} ${milestoneCfg(status).text}`}>
      {milestoneCfg(status).label}
    </span>
  );

  const editBtn = (item: MasterDeliverable) => (
    <button
      onClick={() => setEditingId(item.id)}
      className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 font-medium"
    >
      <Pencil className="w-3.5 h-3.5" /> Edit
    </button>
  );

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">
          Master Deliverables by Vendor
        </h2>
        <span className="text-xs text-gray-400">Edit any row — changes save and flow into the downloaded PowerPoint</span>
      </div>
      <div className="space-y-5">
        {groups.map(group => (
          <div key={group.vendorKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <p className="font-semibold text-gray-900 text-sm">{group.vendorName}</p>
            </div>
            <div className="divide-y divide-gray-100">
              {group.deliverables.map(parent => {
                const hasChildren = parent.children.length > 0;
                const isCollapsed = collapsed.has(parent.id);
                return (
                  <div key={parent.id}>
                    {editingId === parent.id ? (
                      <DeliverableEditForm item={parent} onSave={p => saveEdit(parent.id, p)} onCancel={() => setEditingId(null)} />
                    ) : (
                      <div className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50">
                        <button
                          onClick={() => hasChildren && toggle(parent.id)}
                          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded ${hasChildren ? 'text-gray-500 hover:bg-gray-200' : 'text-transparent cursor-default'}`}
                        >
                          {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-600">
                              Contractual
                            </span>
                            <p className="font-medium text-gray-900 truncate">{parent.deliverableName}</p>
                            {hasChildren && <span className="text-xs text-gray-400">({parent.children.length})</span>}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {statusBadge(parent.derivedStatus)}
                            <span>{parent.completionPct != null ? `${parent.completionPct}%` : '—'}</span>
                            <span>{parent.contractedDueDate || 'No date'}</span>
                            <span>{formatCurrency(parent.milestonePaymentAmount)}</span>
                          </div>
                        </div>
                        {editBtn(parent)}
                      </div>
                    )}
                    {hasChildren && !isCollapsed && (
                      <div className="bg-gray-50/40">
                        {parent.children.map(child => (
                          editingId === child.id ? (
                            <div key={child.id} className="pl-9">
                              <DeliverableEditForm item={child} onSave={p => saveEdit(child.id, p)} onCancel={() => setEditingId(null)} />
                            </div>
                          ) : (
                            <div key={child.id} className="flex items-center gap-3 pl-14 pr-5 py-2.5 hover:bg-gray-100/60">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-700 truncate">{child.deliverableName}</p>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                                  {statusBadge(child.derivedStatus)}
                                  <span>{child.completionPct != null ? `${child.completionPct}%` : '—'}</span>
                                  <span>{child.contractedDueDate || 'No date'}</span>
                                </div>
                              </div>
                              {editBtn(child)}
                              <button
                                onClick={() => onUpdate(child.id, { tier: 'CONTRACTUAL' })}
                                className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Promote
                              </button>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Vendor-based Expenses view ─────────────────────────────────────────────
// Expenses associate automatically by vendor from the reports uploaded in
// Portfolio Reporting — no manual project selection. Multiple vendors roll up
// into a single consolidated portfolio-level burn view.
const VendorExpensesView: React.FC<{
  vendors: VendorSummary[];
  onAttachExpense: (file: File, vendorId?: number | string) => Promise<any>;
}> = ({ vendors, onAttachExpense }) => {
  const rows = vendors.filter(v => v.status === 'ready' || v.status === 'review_required');
  const [attaching, setAttaching] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Manual-pick fallback only appears after an auto-match fails. We hold the
  // last uploaded file so the user can attach it to a chosen vendor without
  // re-selecting the file.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fallbackVendorId, setFallbackVendorId] = useState<string>('');
  const [detectedVendor, setDetectedVendor] = useState<string | null>(null);

  const handlePick = async (file: File) => {
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error('Please upload the .xlsx expense template.');
      return;
    }
    setAttaching(true);
    try {
      // First attempt: auto-match by vendor name from the document.
      const result = await onAttachExpense(file);
      if (result?.needsVendorSelection) {
        // Couldn't auto-match — keep the file and reveal the dropdown.
        setPendingFile(file);
        setDetectedVendor(result.detectedVendor || null);
        setFallbackVendorId('');
      } else {
        setPendingFile(null);
        setDetectedVendor(null);
      }
    } finally {
      setAttaching(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleManualAttach = async () => {
    if (!pendingFile || !fallbackVendorId) return;
    setAttaching(true);
    try {
      const result = await onAttachExpense(pendingFile, fallbackVendorId);
      if (result?.attachedExpenses) {
        setPendingFile(null);
        setDetectedVendor(null);
        setFallbackVendorId('');
      }
    } finally {
      setAttaching(false);
    }
  };

  // Spec: this tab (and Executive Brief Slide 4, which must match it exactly)
  // reflects ONLY the Expenses .xlsx dataset — no fallback to the fees figures
  // carried on the vendor status report. A vendor without an attached weekly
  // expense .xlsx contributes nothing here (renders as "—"), even if it has
  // contract-fee data visible elsewhere (Vendor Status cards, Overview).
  const calc = (v: VendorSummary) => {
    const hasXlsx = v.expensePeriodEnd != null;
    const cap = hasXlsx ? (v.contractCapExpenses ?? 0) : 0;
    const incurred = hasXlsx ? (v.incurredExpensesToDate ?? 0) : 0;
    const pending = hasXlsx ? (v.pendingApprovalExpenses ?? 0) : 0;

    const remaining = cap - incurred - pending; // signed: negative = over-cap
    const hasCap = cap > 0;
    return { cap, incurred, pending, remaining, hasCap };
  };

  const totals = rows.reduce(
    (acc, v) => {
      const c = calc(v);
      acc.cap += c.cap;
      acc.incurred += c.incurred;
      acc.pending += c.pending;
      return acc;
    },
    { cap: 0, incurred: 0, pending: 0 }
  );
  const totalRemaining = Math.max(0, totals.cap - totals.incurred - totals.pending);
  const totalBurn = totals.cap > 0 ? (totals.incurred / totals.cap) * 100 : null;

  if (rows.length === 0) {
    return (
      <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-8 text-center">
        <DollarSign className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <div className="text-sm font-semibold text-gray-700">No vendor expenses yet</div>
        <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
          Upload vendor reports in the Vendors tab first. Then attach each vendor's
          weekly expense .xlsx here to see contract cap, incurred-to-date and burn.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Attach expense workbook — auto-matches the vendor from the document */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Attach Expense File</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload the weekly expense .xlsx — it attaches automatically to the matching
              vendor by name. Its contract cap, incurred and pending budget flow into the
              portfolio view.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePick(f); }}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={attaching}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {attaching ? 'Attaching…' : 'Upload .xlsx'}
            </button>
          </div>
        </div>

        {/* Fallback: shown only when auto-match couldn't confidently pick a vendor */}
        {pendingFile && (
          <div className="mt-4 pt-4 border-t border-amber-200 bg-amber-50 -mx-5 -mb-5 px-5 pb-5 rounded-b-xl">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                Couldn’t match <span className="font-semibold">{pendingFile.name}</span>
                {detectedVendor ? <> (detected “{detectedVendor}”)</> : null} to a vendor automatically.
                Choose which vendor it belongs to.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={fallbackVendorId}
                onChange={(e) => setFallbackVendorId(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white max-w-[260px]"
              >
                <option value="">Select vendor…</option>
                {rows.map((v) => (
                  <option key={v.id} value={String(v.id)}>{v.name}</option>
                ))}
              </select>
              <button
                onClick={handleManualAttach}
                disabled={attaching || !fallbackVendorId}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Attach
              </button>
              <button
                onClick={() => { setPendingFile(null); setDetectedVendor(null); setFallbackVendorId(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Consolidated portfolio-level burn */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Portfolio Expenses</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Consolidated across {rows.length} vendor{rows.length === 1 ? '' : 's'}.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <ExpenseStat label="Contract Cap" value={formatCurrency(totals.cap || undefined)} icon={DollarSign} />
          <ExpenseStat label="Incurred to Date" value={formatCurrency(totals.incurred || undefined)} icon={TrendingUp} />
          <ExpenseStat label="Pending Approval" value={formatCurrency(totals.pending || undefined)} icon={Clock} />
          <ExpenseStat label="Remaining Budget" value={formatCurrency(totalRemaining || undefined)} icon={DollarSign} highlight />
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-600">% Burn (Incurred / Cap)</span>
            <span className="text-sm font-semibold text-gray-900">{totalBurn == null ? '—' : `${totalBurn.toFixed(1)}%`}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${totalBurn == null ? '' : totalBurn >= 90 ? 'bg-red-500' : totalBurn >= 70 ? 'bg-amber-500' : 'bg-indigo-500'}`}
              style={{ width: `${Math.max(0, Math.min(100, totalBurn ?? 0))}%` }}
            />
          </div>
        </div>
      </div>

      {/* Per-vendor breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-3">By Vendor</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-200">
                <th className="py-2 pr-3 font-medium text-gray-600">Vendor</th>
                <th className="py-2 pr-3 font-medium text-gray-600 text-right">Contract Cap</th>
                <th className="py-2 pr-3 font-medium text-gray-600 text-right">Incurred to Date</th>
                <th className="py-2 pr-3 font-medium text-gray-600 text-right">Pending</th>
                <th className="py-2 pr-3 font-medium text-gray-600 text-right">Remaining</th>
                <th className="py-2 pr-3 font-medium text-gray-600 text-right">Burn Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => {
                const c = calc(v);
                const burn = c.hasCap ? (c.incurred / c.cap) * 100 : null;
                return (
                  <tr key={v.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-3 text-gray-900 font-medium">{v.name}</td>
                    <td className="py-2 pr-3 text-right text-gray-700 whitespace-nowrap">{formatCurrency(c.cap || undefined)}</td>
                    <td className="py-2 pr-3 text-right text-gray-700 whitespace-nowrap">{formatCurrency(c.incurred || undefined)}</td>
                    <td className="py-2 pr-3 text-right text-gray-700 whitespace-nowrap">{formatCurrency(c.pending || undefined)}</td>
                    <td className="py-2 pr-3 text-right font-medium whitespace-nowrap">
                      {c.remaining < 0
                        ? <span className="text-red-600">Over Budget<br /><span className="text-xs font-normal">by {formatCurrency(Math.abs(c.remaining))}</span></span>
                        : <span className="text-gray-900">{formatCurrency(c.remaining || undefined)}</span>}
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {burn == null ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <span className={`font-medium ${burn >= 90 ? 'text-red-600' : burn >= 70 ? 'text-amber-600' : 'text-gray-900'}`}>
                          {burn.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ExpenseStat: React.FC<{ label: string; value: string; icon: React.FC<{ className?: string }>; highlight?: boolean }> = ({ label, value, icon: Icon, highlight }) => (
  <div className={`p-3 rounded-lg border ${highlight ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-3.5 h-3.5 ${highlight ? 'text-indigo-600' : 'text-gray-500'}`} />
      <span className="text-xs text-gray-600">{label}</span>
    </div>
    <div className={`text-lg font-semibold ${highlight ? 'text-indigo-700' : 'text-gray-900'}`}>{value}</div>
  </div>
);

function detectFormat(filename: string): FileFormat {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'xlsx' || ext === 'xls') return 'xlsx';
  if (ext === 'csv') return 'csv';
  if (ext === 'docx' || ext === 'doc') return 'docx';
  if (ext === 'pptx' || ext === 'ppt') return 'pptx';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'txt') return 'txt';
  return 'email';
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function Sk({ className }: { className?: string }) {
  return <div className={`bg-gray-200 rounded animate-pulse ${className ?? ''}`} />;
}

function OverviewSkeleton() {
  return (
    <div className="space-y-6">
      {/* RAG banner */}
      <Sk className="h-16 w-full rounded-xl" />

      {/* 4 KPI cards */}
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <Sk className="h-4 w-28" />
              <Sk className="h-8 w-8 rounded-lg" />
            </div>
            <Sk className="h-8 w-20" />
          </div>
        ))}
      </div>

      {/* Vendor cards */}
      <div>
        <Sk className="h-4 w-28 mb-3" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <Sk className="h-4 w-32" />
                <Sk className="h-6 w-20 rounded-full" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Sk className="h-8" />
                <Sk className="h-8" />
              </div>
              <Sk className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── RAG Badge ────────────────────────────────────────────────────────────────

function RAGBadge({ rag }: { rag: RAGStatus | null }) {
  if (!rag) return <span className="text-gray-400 text-xs">—</span>;
  const cfg = RAG_CONFIG[rag];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── Confidence Bar ───────────────────────────────────────────────────────────

function ConfidenceBar({ value }: { value: number | null }) {
  if (value == null) return <span className="text-gray-400 text-xs">—</span>;
  const pct = Math.round(value * 100);
  const color = pct >= 95 ? 'bg-green-500' : pct >= 85 ? 'bg-amber-400' : pct >= 70 ? 'bg-orange-400' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-600 w-8 text-right">{pct}%</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PortfolioReporting() {
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return (t === 'expenses' || t === 'vendors' || t === 'risks' || t === 'milestones' || t === 'overview' || t === 'brief' || t === 'conflicts') ? t : 'overview';
  });
  const [vendors, setVendors] = useState<VendorSummary[]>([]);
  const [risks, setRisks] = useState<RiskRecord[]>([]);
  const [decisions, setDecisions] = useState<DecisionRecord[]>([]);
  const [milestones, setMilestones] = useState<MilestoneRecord[]>([]);
  const [masterGroups, setMasterGroups] = useState<MasterVendorGroup[]>([]);
  const [flaggedFields, setFlaggedFields] = useState<FlaggedField[]>([]);
  const [conflicts, setConflicts] = useState<SourceConflict[]>([]);
  const [resolvingConflictId, setResolvingConflictId] = useState<number | null>(null);
  const [envelopes, setEnvelopes] = useState<EnvelopeRollup[]>([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [deduplicating, setDeduplicating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [deletingVendorId, setDeletingVendorId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploadInstructions, setUploadInstructions] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [showFlagPanel, setShowFlagPanel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inFlightFiles = useRef<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, rRes, dRes, mRes, fRes, eRes, cRes, mdRes] = await Promise.all([
        api.get('/api/portfolio/vendors'),
        api.get('/api/portfolio/risks'),
        api.get('/api/portfolio/decisions'),
        api.get('/api/portfolio/milestones'),
        api.get('/api/portfolio/flagged-fields'),
        api.get('/api/portfolio/envelopes'),
        api.get('/api/portfolio/conflicts'),
        api.get('/api/portfolio/master-deliverables'),
      ]);

      if (vRes.data.success) {
        setVendors(vRes.data.vendors.map((v: any) => ({
          id: String(v.id),
          name: v.vendor_name,
          contractNumber: v.contract_number,
          rag: v.overall_rag as RAGStatus | null,
          status: v.processing_status as VendorStatus,
          fileName: v.file_name || '',
          fileFormat: (v.file_format || 'docx') as FileFormat,
          uploadedAt: v.created_at,
          confidence: v.overall_confidence ? +v.overall_confidence : null,
          flaggedFields: +v.flagged_fields || 0,
          validationErrors: Array.isArray(v.validation_errors) ? v.validation_errors : [],
          contractCapFees: v.contract_cap_fees ? +v.contract_cap_fees : undefined,
          contractCapExpenses: v.contract_cap_expenses ? +v.contract_cap_expenses : undefined,
          incurredFeesToDate: v.incurred_fees_to_date ? +v.incurred_fees_to_date : undefined,
          incurredExpensesToDate: v.incurred_expenses_to_date ? +v.incurred_expenses_to_date : undefined,
          pendingApprovalExpenses: v.pending_approval_expenses ? +v.pending_approval_expenses : undefined,
          budgetImpactThisPeriod: v.budget_impact_this_period ? +v.budget_impact_this_period : undefined,
          reportPeriodEnd: v.report_period_end || undefined,
          expensePeriodEnd: v.expense_period_end || undefined,
          preparedBy: v.prepared_by || undefined,
        })));
      }
      if (rRes.data.success) {
        setRisks(rRes.data.risks.map((r: any) => ({
          riskId: String(r.id),
          vendorName: r.vendor_name,
          severity: r.severity as SeverityLevel,
          track: r.track || '',
          title: r.title,
          impact: r.impact || '',
          owner: r.owner || '',
          status: r.status,
          openSince: r.open_since || '',
        })));
      }
      if (dRes.data.success) {
        setDecisions(dRes.data.decisions.map((d: any) => ({
          decisionId: String(d.id),
          vendorName: d.vendor_name,
          priority: d.priority,
          title: d.title,
          context: d.context || '',
          owner: d.owner || '',
          neededBy: d.needed_by || '',
        })));
      }
      if (mRes.data.success) {
        setMilestones(mRes.data.milestones.map((m: any) => ({
          deliverableId: String(m.id),
          vendorName: m.vendor_name,
          deliverableName: m.deliverable_name,
          contractedDueDate: m.contracted_due_date || '',
          milestonePaymentAmount: m.milestone_payment_amount ? +m.milestone_payment_amount : undefined,
          status: m.derived_status as DeliverableStatus,
          completionPct: m.completion_pct != null ? +m.completion_pct : undefined,
          milestoneType: m.milestone_type || 'SOW_DELIVERABLE',
          isApproximate: m.is_approximate || false,
          changeLog: Array.isArray(m.change_log) ? m.change_log : [],
        })));
      }
      if (mdRes.data.success) {
        const mapNode = (n: any): MasterDeliverable => ({
          id: n.id,
          vendorName: n.vendor_name,
          vendorKey: n.vendor_key,
          parentId: n.parent_id,
          tier: n.tier,
          deliverableName: n.deliverable_name,
          contractedDueDate: n.contracted_due_date || null,
          milestonePaymentAmount: n.milestone_payment_amount ? +n.milestone_payment_amount : undefined,
          derivedStatus: n.derived_status as DeliverableStatus,
          completionPct: n.completion_pct != null ? +n.completion_pct : undefined,
          milestoneType: n.milestone_type || 'SOW_DELIVERABLE',
          children: Array.isArray(n.children) ? n.children.map(mapNode) : [],
        });
        setMasterGroups((mdRes.data.vendors || []).map((g: any) => ({
          vendorName: g.vendorName,
          vendorKey: g.vendorKey,
          deliverables: (g.deliverables || []).map(mapNode),
        })));
      }
      if (fRes.data.success) {
        setFlaggedFields(fRes.data.flaggedFields.map((f: any) => ({
          vendorId: String(f.vendor_report_id),
          vendorName: f.vendor_name,
          fieldName: f.field_name,
          confidence: +f.confidence,
          extractedValue: f.extracted_value || '',
          sourceText: f.source_text || '',
        })));
      }
      if (eRes.data.success) {
        setEnvelopes(eRes.data.envelopes);
      }
      if (cRes.data.success) {
        setConflicts((cRes.data.conflicts || []).map((c: any) => ({
          id: c.id,
          vendorReportId: c.vendor_report_id,
          vendorName: c.vendor_name,
          fieldKey: c.field_key,
          fieldLabel: c.field_label,
          conflictType: c.conflict_type,
          candidates: Array.isArray(c.candidates) ? c.candidates : [],
          autoSuggested: c.auto_suggested ?? null,
          chosenValue: c.chosen_value ?? null,
          resolved: !!c.resolved,
        })));
      }
    } catch {
      // silently fail — data loads via upload if API unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeduplicate = async () => {
    setDeduplicating(true);
    try {
      const res = await api.post('/api/portfolio/deduplicate-risks');
      const { mergedCount } = res.data;
      if (mergedCount > 0) {
        toast.success(`${mergedCount} duplicate risk${mergedCount !== 1 ? 's' : ''} merged`);
        await loadData();
      } else {
        toast('No duplicate risks found', { icon: 'ℹ️' });
      }
    } catch {
      toast.error('Deduplication failed');
    } finally {
      setDeduplicating(false);
    }
  };

  // Optimistic local patchers for the Executive Brief inline edits.
  const patchRisk = useCallback((id: string, patch: Partial<RiskRecord>) => {
    setRisks(prev => prev.map(r => (r.riskId === id ? { ...r, ...patch } : r)));
  }, []);
  const patchDecision = useCallback((id: string, patch: Partial<DecisionRecord>) => {
    setDecisions(prev => prev.map(d => (d.decisionId === id ? { ...d, ...patch } : d)));
  }, []);
  const patchMilestone = useCallback((id: string, patch: Partial<MilestoneRecord>) => {
    setMilestones(prev => prev.map(m => (m.deliverableId === id ? { ...m, ...patch } : m)));
  }, []);

  const updateMasterDeliverable = useCallback(async (id: number, patch: Record<string, any>) => {
    try {
      await api.patch(`/api/portfolio/master-deliverables/${id}`, patch);
      await loadData();
      toast.success('Deliverable updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Update failed');
    }
  }, [loadData]);

  const portfolioRAG: RAGStatus | null = vendors.length === 0 ? null :
    vendors.some(v => v.rag === 'ATTENTION') ? 'ATTENTION' :
    vendors.some(v => v.rag === 'MONITOR') ? 'MONITOR' : 'ON_TRACK';

  const totalAuthorized = vendors.reduce((s, v) => s + (v.contractCapFees ?? 0) + (v.contractCapExpenses ?? 0), 0);
  const totalIncurred = vendors.reduce((s, v) => s + (v.incurredFeesToDate ?? 0) + (v.incurredExpensesToDate ?? 0), 0);
  const totalFlagged = flaggedFields.length;
  const readyVendors = vendors.filter(v => v.status === 'ready' || v.status === 'review_required').length;
  const reviewVendors = vendors.filter(v => v.status === 'review_required').length;

  // ── Upload handlers ────────────────────────────────────────────────────────

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    const allowed = fileArr.filter(f => /\.(xlsx|xls|docx|doc|pptx|ppt|csv|txt|pdf)$/i.test(f.name));
    if (allowed.length === 0) {
      toast.error('Supported types: Word, PowerPoint, Excel, CSV, TXT, PDF');
      return;
    }

    // Guard against the same file being ingested twice from a duplicate event
    // (click bubbling / re-render). Key by name+size+lastModified.
    const keyOf = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;
    const fresh = allowed.filter(f => !inFlightFiles.current.has(keyOf(f)));
    if (fresh.length === 0) return;
    fresh.forEach(f => inFlightFiles.current.add(keyOf(f)));

    // Add ALL placeholders at once so the full list appears immediately
    const entries = fresh.map(file => ({
      file,
      tempId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`,
    }));

    const placeholders: VendorSummary[] = entries.map(({ file, tempId }) => ({
      id: tempId,
      name: file.name.replace(/\.[^.]+$/, ''),
      fileName: file.name,
      fileFormat: detectFormat(file.name),
      rag: null,
      status: 'processing',
      uploadedAt: new Date().toISOString(),
      confidence: null,
      flaggedFields: 0,
      validationErrors: [],
    }));

    setVendors(prev => [...prev, ...placeholders]);
    setUploadingFiles(prev => [...prev, ...entries.map(e => e.tempId)]);

    // Process all files in parallel — vendor row updated optimistically, child data loaded from DB after all complete
    await Promise.all(entries.map(async ({ file, tempId }) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadInstructions.trim()) formData.append('instructions', uploadInstructions.trim());
        const res = await api.post('/api/portfolio/ingest', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const data = res.data;
        // Update vendor row from ingest response (fast, optimistic)
        setVendors(prev => prev.map(v => v.id === tempId ? {
          ...v,
          id: data.vendorId ?? tempId,
          name: data.vendorName ?? v.name,
          contractNumber: data.contractNumber,
          rag: data.rag ?? null,
          status: (data.flaggedFields?.length > 0) ? 'review_required' : 'ready',
          confidence: data.overallConfidence ?? null,
          flaggedFields: data.flaggedFields?.length ?? 0,
          contractCapFees: data.financial?.contractCapFees,
          contractCapExpenses: data.financial?.contractCapExpenses,
          incurredFeesToDate: data.financial?.incurredFeesToDate,
          incurredExpensesToDate: data.financial?.incurredExpensesToDate,
        } : v));

        toast.success(`${file.name} ingested`);
      } catch {
        setVendors(prev => prev.map(v => v.id === tempId ? { ...v, status: 'error' } : v));
        toast.error(`Failed to process ${file.name}`);
      } finally {
        setUploadingFiles(prev => prev.filter(id => id !== tempId));
        inFlightFiles.current.delete(keyOf(file));
      }
    }));

    // Reload all child data from DB after all uploads finish (avoids field-name mismatch in ingest response)
    await loadData();
  }, [loadData, uploadInstructions]);

  // Attach an expense workbook's budget figures to a specific vendor (chosen in
  // the Expenses tab). Sends expenseVendorId so the backend skips the AI status
  // ingest and updates that vendor's budget fields directly.
  // Returns the backend envelope so the caller can reveal the manual-pick
  // dropdown when auto-match needs help. vendorId is omitted on the first
  // (auto-match) attempt and supplied only on the manual fallback re-upload.
  const handleAttachExpense = useCallback(async (file: File, vendorId?: number | string) => {
    const form = new FormData();
    form.append('file', file);
    if (vendorId !== undefined && vendorId !== '') {
      form.append('expenseVendorId', String(vendorId));
    }
    try {
      const res = await api.post('/api/portfolio/ingest', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data || {};
      if (data.attachedExpenses) {
        toast.success(
          data.autoMatched
            ? `Expenses matched to ${data.vendorName}`
            : `Expenses attached to ${data.vendorName}`
        );
        await loadData();
      } else if (data.needsVendorSelection) {
        // Don't toast as an error — the view will prompt for a manual pick.
        if (data.warnings && data.warnings[0]) toast(data.warnings[0], { icon: '⚠️' });
      } else {
        toast.error((data.warnings && data.warnings[0]) || 'Could not attach expenses.');
      }
      return data;
    } catch (err: any) {
      toast.error(`Failed to attach expenses: ${err?.response?.data?.error || err.message}`);
      return { error: true };
    }
  }, [loadData]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleGenerateReport = async () => {
    if (readyVendors === 0) {
      toast.error('No vendors ready for report generation');
      return;
    }
    if (flaggedFields.length > 0) {
      toast(`Generating report with ${flaggedFields.length} flagged field${flaggedFields.length !== 1 ? 's' : ''} — these will be marked for review in the slides.`, { icon: '⚠️', duration: 4000 });
    }
    setGeneratingReport(true);
    try {
      const res = await api.post('/api/portfolio/generate-report', {
        vendorIds: vendors.filter(v => v.status === 'ready' || v.status === 'review_required').map(v => v.id),
        flaggedFields: flaggedFields.map(f => ({ vendorId: f.vendorId, vendorName: f.vendorName, fieldName: f.fieldName, extractedValue: f.extractedValue, sourceText: f.sourceText })),
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Portfolio_Executive_Brief_${new Date().toISOString().slice(0, 10)}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Executive Brief generated!');
    } catch {
      toast.error('Report generation failed');
    } finally {
      setGeneratingReport(false);
    }
  };

  const removeVendor = async (id: string) => {
    setDeletingVendorId(id);
    try {
      await api.delete(`/api/portfolio/vendors/${id}`);
      // Backend CASCADE-deletes the vendor's risks, decisions, milestones,
      // accomplishments and flagged fields. Reload from the server so every tab
      // count reflects the DB — manual per-array filtering missed decisions and
      // milestones and left their counts stale.
      await loadData();
    } catch {
      toast.error('Failed to delete vendor');
    } finally {
      setDeletingVendorId(null);
    }
  };

  const resolveConflict = async (conflictId: number, chosenValue: string, chosenSource: string) => {
    if (!chosenValue || !chosenValue.trim()) {
      toast.error('Pick or enter a value first');
      return;
    }
    setResolvingConflictId(conflictId);
    try {
      await api.patch(`/api/portfolio/conflicts/${conflictId}/resolve`, {
        chosen_value: chosenValue,
        chosen_source: chosenSource,
      });
      toast.success('Conflict resolved');
      await loadData();
    } catch {
      toast.error('Failed to resolve conflict');
    } finally {
      setResolvingConflictId(null);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.delete('/api/portfolio/reset');
      setVendors([]);
      setRisks([]);
      setDecisions([]);
      setMilestones([]);
      setMasterGroups([]);
      setFlaggedFields([]);
      setConflicts([]);
      setEnvelopes([]);
      setShowResetModal(false);
      toast.success('Weekly data cleared — ready for new uploads');
    } catch {
      toast.error('Reset failed');
    } finally {
      setResetting(false);
    }
  };

  // ─── Tabs ──────────────────────────────────────────────────────────────────

  const TABS: { id: Tab; label: string; count?: number }[] = [
    { id: 'overview',   label: 'Overview' },
    { id: 'brief',      label: 'Brief' },
    { id: 'vendors',    label: 'Vendors',   count: vendors.length },
    { id: 'risks',      label: 'Risks & Decisions', count: risks.filter(r => r.status === 'OPEN').length + decisions.length || undefined },
    { id: 'milestones', label: 'Milestones', count: milestones.length || undefined },
    { id: 'expenses',   label: 'Expenses' },
    { id: 'conflicts',  label: 'Conflicts', count: conflicts.filter(c => !c.resolved).length || undefined },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-gray-50">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-8 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Portfolio Reporting</h1>
              <p className="text-sm text-gray-500">Multi-vendor executive status consolidation</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {totalFlagged > 0 && (
              <button
                onClick={() => setShowFlagPanel(!showFlagPanel)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
              >
                <Flag className="w-4 h-4" />
                {totalFlagged} fields need review
              </button>
            )}
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {vendors.length > 0 && (
              <button
                onClick={() => setShowResetModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
                title="Clear all data for new week"
              >
                <Trash2 className="w-4 h-4" />
                New Week
              </button>
            )}
            <button
              onClick={handleGenerateReport}
              disabled={generatingReport || readyVendors === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generatingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Generate Executive Brief
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mt-5">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Confidence Flag Panel ──────────────────────────────────────────── */}
      {showFlagPanel && flaggedFields.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-8 py-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <p className="text-sm font-semibold text-amber-800">Fields requiring human review (confidence &lt; 85%)</p>
            </div>
            <button onClick={() => setShowFlagPanel(false)} className="text-amber-500 hover:text-amber-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-amber-700 text-xs font-semibold uppercase tracking-wide">
                  <th className="text-left pb-2 pr-4">Vendor</th>
                  <th className="text-left pb-2 pr-4">Field</th>
                  <th className="text-left pb-2 pr-4">Extracted Value</th>
                  <th className="text-left pb-2 pr-4">Confidence</th>
                  <th className="text-left pb-2">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {flaggedFields.map((f, i) => (
                  <tr key={i} className="text-gray-700">
                    <td className="py-2 pr-4 font-medium">{f.vendorName}</td>
                    <td className="py-2 pr-4 text-gray-600">{f.fieldName}</td>
                    <td className="py-2 pr-4">{f.extractedValue}</td>
                    <td className="py-2 pr-4">
                      <ConfidenceBar value={f.confidence} />
                    </td>
                    <td className="py-2 text-xs text-gray-500 max-w-xs truncate">{f.sourceText}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-8">

        {/* ── Overview Tab ─────────────────────────────────────────────────── */}
        {activeTab === 'overview' && loading && <OverviewSkeleton />}

        {activeTab === 'overview' && !loading && (
          <div className="space-y-6">

            {/* Portfolio RAG Banner — only when vendors are loaded */}
            {portfolioRAG && (
              <div className={`rounded-xl px-6 py-4 flex items-center gap-4 ${RAG_CONFIG[portfolioRAG].bg} border border-opacity-30`}>
                <span className={`w-3 h-3 rounded-full ${RAG_CONFIG[portfolioRAG].dot}`} />
                <div>
                  <p className={`font-bold text-lg ${RAG_CONFIG[portfolioRAG].text}`}>
                    Portfolio Status: {RAG_CONFIG[portfolioRAG].label}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {readyVendors} of {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} processed
                    {reviewVendors > 0 ? ` · ${reviewVendors} pending review` : ''}
                  </p>
                </div>
              </div>
            )}

            {/* Financial Metric Cards — only when there is data */}
            {vendors.length > 0 && (
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Total Authorized', value: formatCurrency(totalAuthorized || undefined), icon: DollarSign, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                  { label: 'Total Incurred',   value: formatCurrency(totalIncurred || undefined),   icon: TrendingUp,  color: 'text-green-600',  bg: 'bg-green-50'  },
                  { label: 'Remaining',        value: formatCurrency(totalAuthorized && totalIncurred ? totalAuthorized - totalIncurred : undefined), icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
                  { label: 'Burn Rate',        value: formatPct(totalIncurred || undefined, totalAuthorized || undefined), icon: BarChart3, color: 'text-blue-600', bg: 'bg-blue-50' },
                ].map(card => (
                  <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-gray-500 font-medium">{card.label}</p>
                      <div className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}>
                        <card.icon className={`w-4 h-4 ${card.color}`} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state — no vendors yet */}
            {vendors.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
                  <BarChart3 className="w-8 h-8 text-indigo-400" />
                </div>
                <p className="text-lg font-semibold text-gray-700 mb-1">No portfolio data yet</p>
                <p className="text-sm text-gray-400 mb-5">Upload vendor reports to see financial metrics, risks, and milestone tracking.</p>
                <button
                  onClick={() => setActiveTab('vendors')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Upload Vendor Reports
                </button>
              </div>
            )}

            {/* Vendor RAG Cards Grid */}
            {vendors.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Vendor Status</h2>
                <div className="grid grid-cols-3 gap-4">
                  {vendors.map(v => {
                    const Icon = FILE_ICON[v.fileFormat];
                    return (
                      <div key={v.id} className="bg-white rounded-xl border border-gray-200 p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <p className="font-semibold text-gray-900 truncate text-sm">{v.name}</p>
                          </div>
                          <RAGBadge rag={v.rag} />
                        </div>
                        {v.contractNumber && (
                          <p className="text-xs text-gray-500 mb-2">Contract: {v.contractNumber}</p>
                        )}
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                          <div>
                            <p className="text-gray-400">Cap</p>
                            <p className="font-medium">{formatCurrency((v.contractCapFees ?? 0) + (v.contractCapExpenses ?? 0) || undefined)}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Incurred</p>
                            <p className="font-medium">{formatCurrency((v.incurredFeesToDate ?? 0) + (v.incurredExpensesToDate ?? 0) || undefined)}</p>
                          </div>
                        </div>
                        {v.status === 'processing' && (
                          <div className="flex items-center gap-2 text-xs text-blue-600">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Processing...
                          </div>
                        )}
                        {v.status === 'review_required' && (
                          <div className="flex items-center gap-2 text-xs text-amber-600">
                            <AlertCircle className="w-3 h-3" />
                            {v.flaggedFields} field{v.flaggedFields !== 1 ? 's' : ''} need review
                          </div>
                        )}
                        {v.status === 'ready' && (
                          <div className="flex items-center gap-2 text-xs text-green-600">
                            <CheckCircle2 className="w-3 h-3" />
                            Ready
                          </div>
                        )}
                        {v.status === 'error' && (
                          <div className="flex items-center gap-2 text-xs text-red-600">
                            <AlertTriangle className="w-3 h-3" />
                            Processing failed
                          </div>
                        )}
                        {v.validationErrors.filter(e => e.startsWith('VAGUE_TIMELINE') || e.startsWith('MISSING_REQUIRED_FIELD') || e.startsWith('MISSING_DATE') || e.startsWith('PARSING_CONFLICT')).length > 0 && (
                          <div className="mt-2 border-t border-amber-100 pt-2 space-y-1">
                            {v.validationErrors
                              .filter(e => e.startsWith('VAGUE_TIMELINE') || e.startsWith('MISSING_REQUIRED_FIELD') || e.startsWith('MISSING_DATE') || e.startsWith('PARSING_CONFLICT'))
                              .map((e, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
                                  <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                  <span>{e.replace(/^[A-Z_]+:\s*/, '')}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Investment Envelope Roll-up */}
            {envelopes.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Investment Envelopes</h2>
                <div className="grid grid-cols-2 gap-4">
                  {envelopes.map(env => (
                    <div key={env.envelopeId} className="bg-white rounded-xl border border-gray-200 p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{env.envelopeName}</p>
                          {env.objective && <p className="text-xs text-gray-500 mt-0.5">{env.objective}</p>}
                        </div>
                        <RAGBadge rag={env.rag} />
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-xs text-gray-600 mb-3">
                        <div>
                          <p className="text-gray-400">Authorized</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(env.authorizedBudget)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Incurred</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(env.incurred)}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Burn</p>
                          <p className="font-semibold text-gray-900">{env.burnPct}%</p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${env.burnPct >= 85 ? 'bg-red-500' : env.burnPct >= 60 ? 'bg-amber-400' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(env.burnPct, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-2">{env.vendorCount} vendor{env.vendorCount !== 1 ? 's' : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Risks */}
            {risks.filter(r => r.status === 'OPEN').length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Top Open Risks</h2>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Risk</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Impact</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {risks.filter(r => r.status === 'OPEN').slice(0, 4).map(r => (
                        <tr key={r.riskId} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${severityCfg(r.severity).bg} ${severityCfg(r.severity).text}`}>
                              {r.severity}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-gray-600">{r.vendorName}</td>
                          <td className="px-5 py-3 font-medium text-gray-900">{r.title}</td>
                          <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{r.impact}</td>
                          <td className="px-5 py-3 text-gray-600">{r.owner}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Brief Tab (live, interactive executive brief) ─────────────────── */}
        {activeTab === 'brief' && (
          <Suspense fallback={<OverviewSkeleton />}>
            <ExecutiveBriefView
              vendors={vendors}
              risks={risks}
              decisions={decisions}
              milestones={milestones}
              onRefresh={loadData}
              onDownload={handleGenerateReport}
              downloading={generatingReport}
              patchRisk={patchRisk as any}
              patchDecision={patchDecision as any}
              patchMilestone={patchMilestone as any}
              unresolvedConflictCount={conflicts.filter(c => !c.resolved).length}
            />
          </Suspense>
        )}

        {/* ── Vendors Tab ──────────────────────────────────────────────────── */}
        {activeTab === 'vendors' && (
          <div className="space-y-6">

            {/* Upload Instructions */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Instructions for this upload <span className="font-normal text-gray-400">(optional — applies to all files in this upload)</span>
              </label>
              <textarea
                value={uploadInstructions}
                onChange={e => setUploadInstructions(e.target.value)}
                rows={2}
                placeholder="e.g. Emphasize page 3 and 4; ignore the appendix; use the due date from the contract page, not the cover; cross-reference deliverables against the master table."
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 resize-y"
              />
            </div>

            {/* Upload Area */}
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                dragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 bg-white hover:border-indigo-300 hover:bg-gray-50'
              }`}
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-700">Drop vendor reports here or click to upload</p>
              <p className="text-xs text-gray-500 mt-1">Supports Word, PowerPoint, Excel, CSV, TXT, and PDF</p>
              <p className="text-xs text-gray-400 mt-1">Multiple files supported — one per vendor</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".xlsx,.xls,.docx,.doc,.pptx,.ppt,.csv,.txt,.pdf"
                className="hidden"
                onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {/* Vendor Table */}
            {vendors.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-900">{vendors.length} Vendor{vendors.length !== 1 ? 's' : ''}</h2>
                  <p className="text-sm text-gray-500">{readyVendors} ready · {reviewVendors} need review</p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">File</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">RAG</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Confidence</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Flags</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {vendors.map(v => {
                      const Icon = FILE_ICON[v.fileFormat];
                      return (
                        <tr key={v.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3 font-medium text-gray-900">{v.name}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-1.5 text-gray-500">
                              <Icon className="w-4 h-4" />
                              <span className="text-xs truncate max-w-[160px]">{v.fileName}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            {v.status === 'processing' && <span className="flex items-center gap-1.5 text-blue-600 text-xs"><Loader2 className="w-3 h-3 animate-spin" />Processing</span>}
                            {v.status === 'ready' && <span className="flex items-center gap-1.5 text-green-600 text-xs"><CheckCircle2 className="w-3 h-3" />Ready</span>}
                            {v.status === 'review_required' && <span className="flex items-center gap-1.5 text-amber-600 text-xs"><AlertCircle className="w-3 h-3" />Review Required</span>}
                            {v.status === 'error' && <span className="flex items-center gap-1.5 text-red-600 text-xs"><AlertTriangle className="w-3 h-3" />Failed</span>}
                            {v.status === 'pending' && <span className="text-gray-400 text-xs">Pending</span>}
                          </td>
                          <td className="px-5 py-3"><RAGBadge rag={v.rag} /></td>
                          <td className="px-5 py-3 w-36"><ConfidenceBar value={v.confidence} /></td>
                          <td className="px-5 py-3">
                            {v.flaggedFields > 0 ? (
                              <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                                <Flag className="w-3 h-3" />{v.flaggedFields}
                              </span>
                            ) : v.status === 'ready' ? (
                              <span className="text-green-500 text-xs">—</span>
                            ) : <span className="text-gray-400 text-xs">—</span>}
                          </td>
                          <td className="px-5 py-3">
                            <button
                              onClick={() => removeVendor(v.id)}
                              disabled={deletingVendorId === v.id}
                              className="text-gray-400 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Remove vendor"
                            >
                              {deletingVendorId === v.id
                                ? <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                                : <Trash2 className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {vendors.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No vendor reports uploaded yet</p>
              </div>
            )}
          </div>
        )}

        {/* ── Risks & Decisions Tab ─────────────────────────────────────────── */}
        {activeTab === 'risks' && (
          <div className="space-y-6">

            {/* Risks Table */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide">
                  Open Risks ({risks.filter(r => r.status === 'OPEN').length})
                </h2>
                {risks.length > 1 && (
                  <button
                    onClick={handleDeduplicate}
                    disabled={deduplicating}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                  >
                    {deduplicating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Deduplicate Risks
                  </button>
                )}
              </div>
              {risks.filter(r => r.status === 'OPEN').length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Track</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Risk</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Impact</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Open Since</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {risks.filter(r => r.status === 'OPEN').map(r => (
                        <tr key={r.riskId} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${severityCfg(r.severity).bg} ${severityCfg(r.severity).text}`}>
                              {r.severity}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-gray-600">{r.vendorName}</td>
                          <td className="px-5 py-3 text-gray-600">{r.track}</td>
                          <td className="px-5 py-3 font-medium text-gray-900">{r.title}</td>
                          <td className="px-5 py-3 text-gray-600 max-w-xs">{r.impact}</td>
                          <td className="px-5 py-3 text-gray-600">{r.owner}</td>
                          <td className="px-5 py-3 text-gray-500 text-xs">{r.openSince}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 py-10 text-center text-gray-400 text-sm">
                  No open risks
                </div>
              )}
            </div>

            {/* Decisions Table */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                Decisions Required ({decisions.length})
              </h2>
              {decisions.length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Priority</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Decision</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Context</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Needed By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {decisions.map(d => (
                        <tr key={d.decisionId} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${severityCfg(d.priority).bg} ${severityCfg(d.priority).text}`}>
                              {d.priority}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-gray-600">{d.vendorName}</td>
                          <td className="px-5 py-3 font-medium text-gray-900">{d.title}</td>
                          <td className="px-5 py-3 text-gray-600 max-w-xs">{d.context}</td>
                          <td className="px-5 py-3 text-gray-600">{d.owner}</td>
                          <td className="px-5 py-3 text-gray-500 text-xs">{d.neededBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 py-10 text-center text-gray-400 text-sm">
                  No decisions required
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Milestones Tab ───────────────────────────────────────────────── */}
        {activeTab === 'milestones' && (
          <div>
            <MasterDeliverablesTree groups={masterGroups} onUpdate={updateMasterDeliverable} />
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3 mt-8">
              Deliverable Milestone Outlook ({milestones.length})
            </h2>
            {milestones.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Deliverable</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Due Date</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">% Complete</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {milestones.map(m => {
                      const typeCfg = MILESTONE_TYPE_CFG[m.milestoneType ?? 'SOW_DELIVERABLE'] ?? MILESTONE_TYPE_CFG['SOW_DELIVERABLE'];
                      const revCount = m.changeLog?.length ?? 0;
                      return (
                      <tr key={m.deliverableId} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-600">{m.vendorName}</td>
                        <td className="px-5 py-3">
                          <p className="font-medium text-gray-900">{m.deliverableName}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${typeCfg.cls}`}>
                              {typeCfg.label}
                            </span>
                            {revCount > 0 && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">
                                &#916; {revCount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${milestoneCfg(m.status).bg} ${milestoneCfg(m.status).text}`}>
                            {milestoneCfg(m.status).label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-gray-600 text-xs">{m.contractedDueDate}</td>
                        <td className="px-5 py-3 text-gray-600">
                          {m.completionPct != null ? `${m.isApproximate ? '~' : ''}${m.completionPct}%` : '—'}
                        </td>
                        <td className="px-5 py-3 text-gray-600">{formatCurrency(m.milestonePaymentAmount)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 py-10 text-center text-gray-400 text-sm">
                No milestones yet
              </div>
            )}
          </div>
        )}

        {/* ── Expenses Tab ─────────────────────────────────────────────────── */}
        {activeTab === 'expenses' && (
          <VendorExpensesView vendors={vendors} onAttachExpense={handleAttachExpense} />
        )}

        {/* ── Conflicts Tab ────────────────────────────────────────────────── */}
        {activeTab === 'conflicts' && (
          <ConflictResolutionPanel
            conflicts={conflicts}
            resolvingId={resolvingConflictId}
            onResolve={resolveConflict}
          />
        )}
      </div>

      {/* ── Reset Confirmation Modal ─────────────────────────────────────── */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Start New Week?</h2>
                <p className="text-sm text-gray-500">This will permanently delete all current vendor data</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6 text-sm text-amber-800">
              <strong>{vendors.length} vendor report{vendors.length !== 1 ? 's' : ''}</strong>,{' '}
              <strong>{risks.length} risk{risks.length !== 1 ? 's' : ''}</strong>,{' '}
              <strong>{milestones.length} milestone{milestones.length !== 1 ? 's' : ''}</strong> will be removed.
              Make sure you've downloaded the Executive Brief before resetting.
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowResetModal(false)}
                disabled={resetting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={resetting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {resetting ? 'Clearing...' : 'Yes, Clear All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
